/*

Copyright (c) Adam Oliver &lt;mail@adamoliver.net&gt;

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.

*/

import videojs from "video.js";
import Player from "video.js/dist/types/player.js";

// Default options for the plugin.
const defaults = {
  events: [],
  assetName: "Video",
  defaultVideoCategory: "Video",
  defaultAudioCategory: "Audio",
};

export const analyticsModeGoogleTags = "GTAG";

export interface AnalyticsOptions {
  events: Array<AnalyticsEvent>;
  assetName: string;
  defaultVideoCategory?: string;
  defaultAudioCategory?: string;
  mode?: string;
}

export interface AnalyticsEvent {
  name?: string;
  label?: object;
  action: string;
}

export interface AnalyticsPlayer extends Player.default {
  analytics(opts: AnalyticsOptions): void;
}

const analytics = function (this: Player.default, options: AnalyticsOptions) {
  options = videojs.obj.merge(defaults, options);

  this.ready(() => {
    const player = this;

    function track(action: string, props: any) {
      props = props || {}
      let category = options.defaultVideoCategory;

      if (player.isAudio()) {
        category = options.defaultAudioCategory;
      }

      if (options.mode === analyticsModeGoogleTags) {
        props["category"] = category;
        window.gtag("event", action, props);
      } else {
        window.ga("send", "event", category, action, props["label"]);
      }
    }

    function fullscreenchange(event: AnalyticsEvent) {
      const status = player.isFullscreen() ? "open" : "closed";
      track(event.action, { status: status });
    }

    let progress = {
      quarter: false,
      half: false,
      threeQuarters: false,
    };

    function timeupdate(action: string) {
      const percent = playerProgressPercent(player);

      if (!progress.quarter && percent > 25) {
        track(action, { complete: "25", exact: percent });
        progress.quarter = true;
      }

      if (!progress.half && percent > 50) {
        track(action, { complete: "50", exact: percent });
        progress.half = true;
      }

      if (!progress.threeQuarters && percent > 75) {
        track(action, { complete: "75", exact: percent });
        progress.threeQuarters = true;
      }
    }

    const eventNames = options.events.map((it) => {
      return it.name || it.action;
    });

    for (let index = 0; index < eventNames.length; index++) {
      const kind = eventNames[index];
      const eventsOfKind = options.events.filter((event) => {
        return (event.name || event.action) === kind;
      });
      if (eventsOfKind.length == 0) continue;

      const event = eventsOfKind[0];
      switch(kind) {
        case "play":
        case "pause":
        case "ended":
          player.one(kind, () => {
            if( kind == "play") {
              track("Asset", { name: options.assetName });
            }
            track(event.action, event.label);
          });

        break

        default:
          player.on(kind, () => {
            switch (kind) {
              case "fullscreenchange":
                fullscreenchange(event);
                break;
              case "timeupdate":
                timeupdate(event.action);
                break;
              default:
                track(event.action, event.label);
                break;
            }
          });
      }

      options.events = options.events.filter((event) => {
        return event.name !== kind;
      });
    }
  });
};

function playerProgressPercent(player: Player.default): number {
  let elapsed = Math.round(player.currentTime()!);
  let duration = Math.round(player.duration()!);
  return Math.round((elapsed / duration) * 100);
}

analytics.VERSION = '1.1.0'

videojs.registerPlugin("analytics", analytics);
